import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface DraftEditorProps {
  defaultValue?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

export interface DraftEditorHandle {
  getHTML: () => string;
  setHTML: (html: string) => void;
  getEditor: () => EditorState | null;
}

// Custom Remove Format Button Component
interface CustomRemoveButtonProps {
  onClick: () => void;
}

const CustomRemoveButton: React.FC<CustomRemoveButtonProps> = ({ onClick }) => {
  return (
    <div
      className="rdw-option-wrapper"
      onClick={onClick}
      title="Eliminar todo el formato (solo texto puro)"
      style={{ cursor: 'pointer' }}
    >
      <img
        alt="Remove all formatting"
        src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNSIgaGVpZ2h0PSIxNSIgdmlld0JveD0iMCAwIDE2IDE2Ij48cGF0aCBkPSJNOC4xIDE0bDYuNC03LjJjLjYtLjcuNi0xLjgtLjEtMi41bC0yLjctMi43Yy0uMy0uNC0uOC0uNi0xLjMtLjZIOC42Yy0uNSAwLTEgLjItMS40LjZMLjUgOS4yYy0uNi43LS42IDEuOS4xIDIuNWwyLjcgMi43Yy4zLjQuOC42IDEuMy42SDE2di0xSDguMXptLTEuMy0uMXMwLS4xIDAgMGwtMi43LTIuN2MtLjQtLjQtLjQtLjkgMC0xLjNMNy41IDZoLTFsLTMgMy4zYy0uNi43LS42IDEuNy4xIDIuNEw1LjkgMTRINC42Yy0uMiAwLS40LS4xLS42LS4yTDEuMiAxMWMtLjMtLjMtLjMtLjggMC0xLjFMNC43IDZoMS44TDEwIDJoMUw3LjUgNmwzLjEgMy43LTMuNSA0Yy0uMS4xLS4yLjEtLjMuMnoiLz48L3N2Zz4="
      />
    </div>
  );
};

const DraftEditor = forwardRef<DraftEditorHandle, DraftEditorProps>(
  function DraftEditor({ defaultValue = '', onChange, placeholder = '', readOnly = false }, ref) {
    const [editorState, setEditorState] = useState<EditorState>(() => {
      if (defaultValue) {
        const contentBlock = htmlToDraft(defaultValue);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          return EditorState.createWithContent(contentState);
        }
      }
      return EditorState.createEmpty();
    });

    // Handle editor state changes
    const handleEditorChange = useCallback((newEditorState: EditorState) => {
      setEditorState(newEditorState);

      if (onChange) {
        const rawContentState = convertToRaw(newEditorState.getCurrentContent());
        const html = draftToHtml(rawContentState);
        // Return empty string if content is just empty paragraph
        if (html === '<p></p>\n' || html === '<p></p>') {
          onChange('');
        } else {
          onChange(html);
        }
      }
    }, [onChange]);

    // Handle remove all formatting
    const handleRemoveAllFormatting = useCallback(() => {
      // Get current content
      const contentState = editorState.getCurrentContent();

      // Extract only plain text (removes ALL formatting and HTML)
      const plainText = contentState.getPlainText();

      // Create new content with only the plain text
      const newContentState = ContentState.createFromText(plainText);

      // Create new editor state with the plain text content
      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        'remove-range'
      );

      // Update the editor
      handleEditorChange(newEditorState);
    }, [editorState, handleEditorChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getHTML: () => {
        const rawContentState = convertToRaw(editorState.getCurrentContent());
        const html = draftToHtml(rawContentState);
        // Return empty string if content is just empty paragraph
        if (html === '<p></p>\n' || html === '<p></p>') {
          return '';
        }
        return html;
      },
      setHTML: (html: string) => {
        if (!html) {
          setEditorState(EditorState.createEmpty());
          return;
        }
        const contentBlock = htmlToDraft(html);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      },
      getEditor: () => editorState
    }));

    // Update editor when defaultValue changes
    useEffect(() => {
      if (defaultValue !== undefined) {
        const currentHTML = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        const normalizedCurrent = currentHTML === '<p></p>\n' || currentHTML === '<p></p>' ? '' : currentHTML;
        const normalizedDefault = defaultValue || '';

        // Only update if actually different
        if (normalizedCurrent !== normalizedDefault) {
          if (!defaultValue) {
            setEditorState(EditorState.createEmpty());
          } else {
            const contentBlock = htmlToDraft(defaultValue);
            if (contentBlock) {
              const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
              const newEditorState = EditorState.createWithContent(contentState);
              setEditorState(newEditorState);
            }
          }
        }
      }
    }, [defaultValue]);

    return (
      <div
        style={{
          minHeight: '400px',
          backgroundColor: '#fff',
          border: '1px solid #F1F1F1'
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          placeholder={placeholder}
          readOnly={readOnly}
          stripPastedStyles={true}
          toolbar={{
            options: ['inline', 'list', 'textAlign', 'remove'],
            inline: {
              options: ['bold', 'italic', 'underline']
            },
            list: {
              options: ['unordered', 'ordered']
            },
            textAlign: {
              options: ['left', 'center', 'right', 'justify']
            },
            remove: {
              component: () => <CustomRemoveButton onClick={handleRemoveAllFormatting} />
            }
          }}
          editorStyle={{
            minHeight: '350px',
            padding: '0 1rem'
          }}
        />
      </div>
    );
  }
);

DraftEditor.displayName = 'DraftEditor';

export default DraftEditor;
